import { text, index } from 'drizzle-orm/sqlite-core'
import { defineTable } from '@kubdee/sdk/schema'
import { connectorTypes } from './connectors-types'

// ============================================================================
// connectors_instances — Per-tenant installations of a connector type
//
// One row per "installed connection" (e.g. a company's FB page, a tenant's
// Gmail inbox). Company scoped. Soft-delete supported.
// ============================================================================
export const connectorInstances = defineTable(
  'connectors',
  'instances',
  {
    id: text('id').primaryKey(),
    companyId: text('company_id').notNull(),
    typeId: text('type_id')
      .notNull()
      .references(() => connectorTypes.id),
    displayName: text('display_name'),
    status: text('status').notNull().default('disconnected'),
    // Denormalised "primary" capability copied from `connectors_types.capabilities[0]`
    // at insert time (or backfilled by migration 008 for legacy rows). Lets the
    // list endpoint push `?capability=messaging` into a SQL WHERE clause via the
    // composite `(company_id, capability_primary, status)` index — avoids reading
    // every row for the company just to post-filter in JS (issue #250 H3).
    // Nullable to tolerate the pre-backfill window + defensive no-capability types.
    capabilityPrimary: text('capability_primary'),
    config: text('config'),
    metadata: text('metadata'),
    // AI agent selected for new conversations on this connector. Chat settings
    // control whether this starts new conversations in AI mode or only acts as
    // a preselected agent for staff. FK target lives in the `ai` module's tenant
    // DB so we deliberately do not declare a Drizzle FK here.
    defaultAiAgentId: text('default_ai_agent_id'),
    lastSyncAt: text('last_sync_at'),
    lastError: text('last_error'),
    disabledReason: text('disabled_reason'),
    createdAt: text('created_at').notNull(),
    createdBy: text('created_by'),
    updatedAt: text('updated_at').notNull(),
    updatedBy: text('updated_by'),
    deletedAt: text('deleted_at'),
    deletedBy: text('deleted_by'),
  },
  (table) => [
    index('connectors_instances_company_status_idx').on(table.companyId, table.status),
    index('connectors_instances_type_id_idx').on(table.typeId),
    index('connectors_instances_company_type_idx').on(table.companyId, table.typeId),
    // Matches the list endpoint's WHERE (company_id, capability_primary [, status]).
    index('connectors_instances_company_capability_status_idx').on(
      table.companyId,
      table.capabilityPrimary,
      table.status,
    ),
  ],
)

export type ConnectorInstance = typeof connectorInstances.$inferSelect
export type NewConnectorInstance = typeof connectorInstances.$inferInsert
